import { useCallback, useEffect, useRef, useState } from 'react';

// A zero-height detection line 51% down the viewport - the acceptance
// criteria's "51% of viewport" activation trigger. A section becomes active
// the instant this line crosses it, rather than when 51% of the section
// itself is visible (which would behave wrong for sections taller than the
// viewport, e.g. the units list).
const ACTIVE_LINE_RATIO = 0.51;
const ACTIVE_LINE_ROOT_MARGIN = `-${ACTIVE_LINE_RATIO * 100}% 0px -${
	(1 - ACTIVE_LINE_RATIO) * 100
}% 0px`;

// A jump-nav click scrolls most targets flush to the top of the viewport
// (Information gets a small 50px offset instead; Overview scrolls to the
// very top of the page - see FacilityPageV2's scrollToSection), not to
// the 51% activation line above. A section shorter than that gap (up to
// the full 51% of the viewport for a flush landing) never straddles the
// line, so the *next* section reports active immediately - stomping the
// tab the user just chose. Trust the click for this long before letting
// the observer override it again.
const CLICK_SETTLE_MS = 800;

// Finds the section currently sitting at the activation line by reading
// layout directly, instead of depending on an IntersectionObserver batch.
// A batch only fires on threshold crossings, so a scroll that ends with
// nothing crossing the line (e.g. the click's target exits while nothing
// else enters in the same frame) never reports what's now active - this
// gives the settle timeout a way to check for itself. Shares
// ACTIVE_LINE_RATIO with ACTIVE_LINE_ROOT_MARGIN above so the two can't
// drift apart.
function findSectionAtActivationLine(sectionIds) {
	const lineY = globalThis.innerHeight * ACTIVE_LINE_RATIO;
	let activeId = null;
	for (const id of sectionIds) {
		const element = document.getElementById(id);
		const rect = element?.getBoundingClientRect();
		if (rect && rect.top <= lineY && rect.bottom > lineY) {
			activeId = id;
		}
	}
	return activeId;
}

// Shared by the scroll-top pin and the click-lock settle timeout below, so
// the two can't disagree about what "at the top" means.
function isAtPageTop() {
	return globalThis.scrollY <= 0;
}

// sectionIds should be a stable array (e.g. a module-level constant) - a new
// array literal on every render would tear down and recreate the observer
// needlessly.
//
// ready should flip from false to true once the section elements are
// actually in the DOM (e.g. once the page has finished its loading state).
// Without it, a caller that's already `enabled` on its first render (like
// FacilityPageV2, whose viewport-width gate defaults to enabled before the
// real width is known) would find zero targets, skip creating the observer,
// and never retry once the sections mount on a later render - `enabled`
// alone wouldn't change to re-trigger this effect.
export default function useActiveSection(
	sectionIds,
	{ enabled = true, ready = true } = {},
) {
	const [activeId, setActiveId] = useState(sectionIds[0] ?? null);
	const pendingClickRef = useRef(null);
	// The most recent section a batch reported while a click was pending but
	// didn't match it - not applied immediately (see below), but not
	// discarded either, so the settle timeout can fall back to it. Only
	// meaningful if userScrolledRef is also true by settle time - see
	// notifyClick for why.
	const lastIgnoredIdRef = useRef(null);
	// Set by an actual wheel/touch gesture during the settle window, as
	// opposed to the programmatic scroll the click itself started. Without
	// this, the settle timeout can't tell "the user scrolled away mid-lock"
	// (should adopt lastIgnoredIdRef) apart from "the click's own scroll
	// simply came to rest past a section too short to straddle the
	// activation line" (should keep the clicked section active) - both look
	// like the same contradicting batch from inside the observer callback.
	const userScrolledRef = useRef(false);
	const settleTimeoutIdRef = useRef(null);
	const detachUserScrollListenersRef = useRef(null);

	useEffect(() => {
		if (!enabled || !ready || typeof IntersectionObserver === 'undefined') {
			return undefined;
		}

		const targets = sectionIds
			.map((id) => ({ id, element: document.getElementById(id) }))
			.filter(({ element }) => element);

		if (targets.length === 0) {
			return undefined;
		}

		const elementToId = new Map(
			targets.map(({ id, element }) => [element, id]),
		);

		const observer = new IntersectionObserver(
			(entries) => {
				const intersectingIds = entries
					.filter((entry) => entry.isIntersecting)
					.map((entry) => elementToId.get(entry.target));

				if (intersectingIds.length === 0) {
					return;
				}

				// The activation line can cross more than one section during a
				// single scroll frame; the lowest section in document order is
				// the one that has actually reached it.
				const lowestActiveId = sectionIds
					.filter((id) => intersectingIds.includes(id))
					.pop();

				const pending = pendingClickRef.current;
				if (pending) {
					if (Date.now() < pending.expiresAt) {
						// A smooth scroll's intermediate frames can transiently
						// intersect the clicked section on the way to resting
						// somewhere else - that's not evidence the scroll has
						// settled, so a match here doesn't release the lock
						// early. Only elapsed time does. Remember what this
						// batch actually reported, though, in case the user
						// scrolled elsewhere and no further batch arrives
						// before the settle timeout checks back in.
						if (lowestActiveId !== pending.id) {
							lastIgnoredIdRef.current = lowestActiveId;
							return;
						}
						lastIgnoredIdRef.current = null;
					} else {
						pendingClickRef.current = null;
					}
				}

				setActiveId((previous) =>
					lowestActiveId && lowestActiveId !== previous
						? lowestActiveId
						: previous,
				);
			},
			{
				root: null,
				rootMargin: ACTIVE_LINE_ROOT_MARGIN,
				threshold: 0,
			},
		);

		targets.forEach(({ element }) => observer.observe(element));

		return () => observer.disconnect();
	}, [sectionIds, enabled, ready]);

	// The first section's own height can be shorter than the activation
	// line's minimum possible position (scrollY 0 + 51% of the viewport) -
	// FacilityPageV2's #overview is exactly this case, since the hero
	// gallery that follows it isn't part of the section. When that happens
	// the observer can never report the first section as active, no matter
	// how far up the page is scrolled, because the line simply never
	// reaches inside it. Pin to the first section directly whenever the
	// page is at the very top, unless a click lock is actively deciding
	// otherwise.
	useEffect(() => {
		if (!enabled || !ready) {
			return undefined;
		}

		const firstSectionId = sectionIds[0];
		if (!firstSectionId) {
			return undefined;
		}

		const onScroll = () => {
			if (isAtPageTop() && !pendingClickRef.current) {
				setActiveId(firstSectionId);
			}
		};

		globalThis.addEventListener('scroll', onScroll, { passive: true });
		onScroll();

		return () => globalThis.removeEventListener('scroll', onScroll);
	}, [sectionIds, enabled, ready]);

	useEffect(() => {
		return () => {
			globalThis.clearTimeout(settleTimeoutIdRef.current);
			detachUserScrollListenersRef.current?.();
		};
	}, []);

	// Called when a jump-nav tab is clicked, so its section reads as active
	// immediately instead of waiting on (and possibly losing to) the
	// IntersectionObserver. Schedules its own re-check for when the lock
	// lapses, rather than relying on some future observer batch to notice -
	// if the user scrolls elsewhere within the window and nothing crosses
	// the activation line again, no such batch would ever arrive.
	const notifyClick = useCallback(
		(sectionId) => {
			globalThis.clearTimeout(settleTimeoutIdRef.current);
			detachUserScrollListenersRef.current?.();

			pendingClickRef.current = {
				id: sectionId,
				expiresAt: Date.now() + CLICK_SETTLE_MS,
			};
			lastIgnoredIdRef.current = null;
			userScrolledRef.current = false;
			setActiveId(sectionId);

			// wheel/touchmove only ever fire from a real scroll gesture, never
			// from the scrollTo the click itself triggers and never from a
			// stationary tap (touchstart alone, e.g. re-tapping a tab or the
			// sticky Reserve button) - the signal that distinguishes "the user
			// took over" from "the click's own scroll settled here".
			const markUserScrolled = () => {
				userScrolledRef.current = true;
			};
			globalThis.addEventListener('wheel', markUserScrolled, {
				passive: true,
			});
			globalThis.addEventListener('touchmove', markUserScrolled, {
				passive: true,
			});
			detachUserScrollListenersRef.current = () => {
				globalThis.removeEventListener('wheel', markUserScrolled);
				globalThis.removeEventListener('touchmove', markUserScrolled);
			};

			settleTimeoutIdRef.current = globalThis.setTimeout(() => {
				pendingClickRef.current = null;
				detachUserScrollListenersRef.current?.();
				detachUserScrollListenersRef.current = null;
				if (userScrolledRef.current) {
					// The scroll-top pin's own rule takes priority here too -
					// the activation line can never reach inside a first
					// section shorter than it (see isAtPageTop's callers),
					// so at rest at the very top, findSectionAtActivationLine
					// would otherwise return null and fall through to
					// whatever the observer last reported for a lower
					// section, exactly the mis-highlight the pin exists to
					// prevent. Otherwise, prefer a fresh read of the DOM over
					// lastIgnoredIdRef - a batch that found nothing
					// intersecting (see findSectionAtActivationLine above)
					// never populates that ref, so relying on it alone would
					// leave the tapped tab active after the user scrolled
					// away.
					const currentId =
						(isAtPageTop() ? sectionIds[0] : null) ||
						findSectionAtActivationLine(sectionIds) ||
						lastIgnoredIdRef.current;
					if (currentId) {
						setActiveId(currentId);
					}
				}
			}, CLICK_SETTLE_MS);
		},
		[sectionIds],
	);

	return [enabled ? activeId : null, notifyClick];
}
